/* eslint-disable react/prop-types */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/self-closing-comp */
import parseHTMLToReact, {
  attributesToProps,
  domToReact,
} from "html-react-parser";
import { Card, Flex, H1, H2, H3, H4, H5, H6, P } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import AICharts from "../AIContent/common-components/AICharts";
import { extractEChartJsonFromProps } from "../AIContent/utils/extractEChartJsonFromProps";
import CanvBarChart from "./AIDashboardComponents/CanvBarChart";
import CanvGroupTypesSelect from "./AIDashboardComponents/CanvGroupTypesSelect";
import CanvLineChart from "./AIDashboardComponents/CanvLineChart";
import CanvOrgChart from "./AIDashboardComponents/CanvOrgChart";
import CanvSelectTeamId from "./AIDashboardComponents/CanvSelectTeamId";
import CanvTeamDescription from "./AIDashboardComponents/CanvTeamDescription";
import CanvTeamDetails from "./AIDashboardComponents/CanvTeamDetails";
import CanvTeamForecastChart from "./AIDashboardComponents/CanvTeamForecastChart";
import CanvTeamMembers from "./AIDashboardComponents/CanvTeamMembers";
import CanvTeamObjectives from "./AIDashboardComponents/CanvTeamObjectives";
import CanvTeamSearch from "./AIDashboardComponents/CanvTeamSearch";
import CanvTeamTable from "./AIDashboardComponents/CanvTeamTable";
import AIDashboardProvider from "./context/AIDashboardProvider";
import { jsonComponentRegistry } from "./utils/jsonComponentRegistry";
import { renderDashboardTree } from "./utils/renderDashboardTree";
import { safeParseDashboardJson } from "./utils/safeParseDashboardJson";

const StyledFlex = styled(Flex)`
  width: 100%;
  overflow: auto;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    a {
      font-size: inherit !important;
      font-weight: inherit !important;
    }
  }
`;

/**
 * Creates a handler for HTML elements that renders them with children
 * @param {string|React.ComponentType} Component - HTML tag name or React component
 * @returns {Function} Handler function for the element
 */
const createElementHandler =
  (Component) =>
  ({ attribs, children, options }) => {
    const props = attributesToProps(attribs);
    return <Component {...props}>{domToReact(children, options)}</Component>;
  };

/**
 * Creates a handler for self-closing HTML elements
 * @param {string|React.ComponentType} Component - HTML tag name or React component
 * @returns {Function} Handler function for the element
 */
const createSelfClosingElementHandler =
  (Component) =>
  ({ attribs }) => {
    const props = attributesToProps(attribs);
    return <Component {...props} />;
  };

/**
 * Creates a handler for self-closing HTML elements with a unique key
 * @param {string|React.ComponentType} Component - HTML tag name or React component
 * @param {Function} getKey - Function to generate key from attribs
 * @returns {Function} Handler function for the element
 */
const createSelfClosingElementHandlerWithKey =
  (Component, getKey) =>
  ({ attribs }) => {
    const props = attributesToProps(attribs);
    const key = getKey ? getKey(attribs) : undefined;
    return <Component key={key} {...props} />;
  };

const baseHTML = {
  // Standard HTML elements
  h1: createElementHandler(H1),
  h2: createElementHandler(H2),
  h3: createElementHandler(H3),
  h4: createElementHandler(H4),
  h5: createElementHandler(H5),
  h6: createElementHandler(H6),
  p: createElementHandler(P),
  div: createElementHandler("div"),
  span: createElementHandler("span"),
  strong: createElementHandler("strong"),
  em: createElementHandler("em"),
  ul: createElementHandler("ul"),
  ol: createElementHandler("ol"),
  li: createElementHandler("li"),
  a: createElementHandler("a"),
  code: createElementHandler("code"),
  pre: createElementHandler("pre"),
  blockquote: createElementHandler("blockquote"),

  // Self-closing elements
  br: createSelfClosingElementHandler("br"),
  hr: createSelfClosingElementHandler("hr"),
  img: createSelfClosingElementHandler("img"),

  // Custom TeamForm components
  "team-search-input": createElementHandler(CanvTeamSearch),
  "selected-team-id": createSelfClosingElementHandlerWithKey(
    CanvSelectTeamId,
    (attribs) =>
      `selected-team-id-${attribs?.channel || attribs?.value || "missing"}`
  ),
  "team-table-input": createElementHandler(CanvTeamTable),
  // Handle dashboard wrapper to ensure children are processed correctly
  dashboard: createElementHandler("div"),
  "team-details-output": createElementHandler(CanvTeamDetails),
  "team-description-output": createElementHandler(CanvTeamDescription),
  "team-objectives-output": createElementHandler(CanvTeamObjectives),
  "team-members-output": createElementHandler(CanvTeamMembers),
  "org-chart-output": createElementHandler(CanvOrgChart),
  "bar-chart-output": createElementHandler(CanvBarChart),
  "line-chart-output": createElementHandler(CanvLineChart),
  "team-types-select-input": createElementHandler(CanvGroupTypesSelect),
  "team-forecast-chart-output": createElementHandler(CanvTeamForecastChart),
  graph: ({ children, attribs }) => (
    <AICharts specs={extractEChartJsonFromProps({ ...attribs, children })} />
  ),
  "e-chart": ({ children, attribs }) => (
    <AICharts specs={extractEChartJsonFromProps({ ...attribs, children })} />
  ),
};

/**
 * @param {Object} [htmlElementOverrides={}]
 * @returns {Object}
 */
const createHtmlParsingOptions = (htmlElementOverrides = {}) => ({
  /**
   * @param {Object} param0
   * @param {Object} param0.attribs
   * @param {React.ReactNode} param0.children
   * @param {string} param0.tagName
   * @returns {React.ReactElement|undefined}
   */
  replace({ attribs, children, tagName }) {
    if (!tagName) {
      return () => {};
    }

    const override = baseHTML[tagName];

    if (override) {
      return override({
        options: createHtmlParsingOptions(htmlElementOverrides),
        children,
        tagName,
        attribs,
      });
      // eslint-disable-next-line no-else-return
    } else {
      return undefined;
    }
  },
});

const AIDashboardCanvas = ({ chatId, dashboardId, content }) => {
  const [Content, setContent] = useState(null);

  useEffect(() => {
    if (!content) {
      setContent(null);
      return;
    }

    const jsonTree = safeParseDashboardJson(content);

    if (jsonTree) {
      setContent(renderDashboardTree(jsonTree, jsonComponentRegistry));
    } else {
      setContent(parseHTMLToReact(content.trim(), createHtmlParsingOptions()));
    }
  }, [content]);

  return (
    <AIDashboardProvider chatId={chatId} dashboardId={dashboardId}>
      <Card>
        <StyledFlex flexDirection="column" gap="r">
          {Content}
        </StyledFlex>
      </Card>
    </AIDashboardProvider>
  );
};

AIDashboardCanvas.propTypes = {
  chatId: PropTypes.string.isRequired,
  dashboardId: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
};

export default AIDashboardCanvas;
